import * as tslib_1 from "tslib";
import { ErrorHandler } from '@angular/core';
import { MapsAPILoader } from '@agm/core';
import { LatLon } from '../util/geodesy/latlon';
import { PlaceResultWrapper } from './place-result-wrapper';
import { ACTIVITY_FILTERS } from '../search/filters/activity-filters';
import { convert, METERS, NAUTICAL_MILES } from '../util/units';
import { MAX_BBOX_AREA } from '../airport/airport.service';
/**
 * Promise-based wrapper for Google Maps/Places API.
 */
var GoogleService = /** @class */ (function () {
    function GoogleService(_loader, _errorHandler) {
        var _this = this;
        this._loader = _loader;
        this._errorHandler = _errorHandler;
        this._loaded = false;
        this._loading = false;
        this._promise = null;
        this._promise = new Promise(function (resolve, reject) {
            _this._resolve = resolve;
            _this._reject = reject;
        });
    }
    Object.defineProperty(GoogleService.prototype, "promise", {
        /**
         * Returns a promise that resolves when the Google service has successfully loaded, or rejected if an error
         * occurred during initialization.
         * @returns {Promise<void>}
         */
        get: function () {
            return this._promise;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the icon URL to use for the given place result.
     * @param result {google.maps.places.PlaceResult} Google place result
     * @param [selectedType] {string} (Optional) If passed in, the icon type will be biased towards the
     *  given place type.
     * @returns {string} Icon type.
     */
    GoogleService.getIconUrl = function (result, selectedType) {
        var type = GoogleService.getIconType(result, selectedType);
        return type ? "assets/icons/" + type + ".png" : null;
    };
    /**
     * Returns the appropriate icon type to use for the given place result.
     * @param result {google.maps.places.PlaceResult} Google place result
     * @param [selectedType] {string} (Optional) If passed in, the icon type will be biased towards the
     *  given place type.
     * @returns {string} Icon type.
     */
    GoogleService.getIconType = function (result, selectedType) {
        // if it is a real Google type being selected, mark all results with that type
        if (selectedType
            && ['restaurant', 'park', 'museum', 'lodging'].includes(selectedType)
            && result.types.includes(selectedType)) {
            return selectedType;
        }
        // otherwise use the icon for that type in the following order
        if (result.types.includes('park')) {
            return 'park';
        }
        if (result.types.includes('restaurant')) {
            return 'restaurant';
        }
        if (result.types.includes('lodging')) {
            return 'lodging';
        }
        if (result.types.includes('car_rental') || result.types.includes('bus_station')
            || result.types.includes('subway_station') || result.types.includes('taxi_stand')
            || result.types.includes('train_station') || result.types.includes('transit_station')) {
            return 'taxi';
        }
        if (result.types.includes('museum')) {
            return 'museum';
        }
        return 'attraction';
    };
    GoogleService.prototype.init = function (attributionEl) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._attributionEl = attributionEl;
                        return [4 /*yield*/, this._load()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    GoogleService.prototype._load = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(!this._loaded && !this._loading)) return [3 /*break*/, 5];
                        this._loading = true;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, this._loader.load()];
                    case 2:
                        _a.sent();
                        this._places = new google.maps.places.PlacesService(this._attributionEl);
                        this._autocomplete = new google.maps.places.AutocompleteService();
                        this._geocoder = new google.maps.Geocoder();
                        this._loaded = true;
                        this._resolve();
                        return [3 /*break*/, 5];
                    case 3:
                        e_1 = _a.sent();
                        this._errorHandler.handleError(e_1);
                        this._reject(e_1);
                        throw e_1;
                    case 4:
                        this._loading = false;
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Retrieves place autocomplete predictions based on the supplied autocomplete request.
     * @param {google.maps.places.AutocompletionRequest} query
     * @returns {Promise<google.maps.places.AutocompletePrediction[]>}
     */
    GoogleService.prototype.getPlacePredictions = function (query) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._promise];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                try {
                                    _this._autocomplete.getPlacePredictions(query, function (results, status) {
                                        if (status === google.maps.places.PlacesServiceStatus.OK) {
                                            return resolve(results);
                                        }
                                        if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                                            return resolve([]);
                                        }
                                        reject(new Error(status));
                                    });
                                }
                                catch (e) {
                                    reject(e);
                                }
                            })];
                }
            });
        });
    };
    /**
     * Retrieves query autocomplete predictions based on the supplied query autocomplete request.
     * @param {google.maps.places.QueryAutocompletionRequest} query
     * @returns {Promise<google.maps.places.QueryAutocompletePrediction[]>}
     */
    GoogleService.prototype.getQueryPredictions = function (query) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._promise];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                try {
                                    _this._autocomplete.getQueryPredictions(query, function (results, status) {
                                        if (status === google.maps.places.PlacesServiceStatus.OK) {
                                            return resolve(results);
                                        }
                                        if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                                            return resolve([]);
                                        }
                                        reject(new Error(status));
                                    });
                                }
                                catch (e) {
                                    reject(e);
                                }
                            })];
                }
            });
        });
    };
    /**
     * Geocode a request.
     * @param {google.maps.GeocoderRequest} request
     * @returns {Promise<google.maps.GeocoderResult[]>}
     */
    GoogleService.prototype.geocode = function (request) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._promise];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                try {
                                    _this._geocoder.geocode(request, function (results, status) {
                                        if (status === google.maps.GeocoderStatus.OK) {
                                            return resolve(results);
                                        }
                                        if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
                                            return resolve([]);
                                        }
                                        reject(new Error(status));
                                    });
                                }
                                catch (e) {
                                    reject(e);
                                }
                            })];
                }
            });
        });
    };
    /**
     * Geocode a place ID, returning the LatLon (or null if geocoding was not successful).
     * @param {string} placeId Google place ID
     * @returns {Promise<LatLon>} LatLon (if geocoding was successful), or null (if no results)
     */
    GoogleService.prototype.geocodePlace = function (placeId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var results, result, location_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._promise];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.geocode({ placeId: placeId })];
                    case 2:
                        results = _a.sent();
                        if (results.length > 0) {
                            result = results[0];
                            location_1 = result.geometry.location;
                            return [2 /*return*/, new LatLon(location_1.lat(), location_1.lng())];
                        }
                        return [2 /*return*/, null];
                }
            });
        });
    };
    /**
     * Retrieves a list of places near a particular location, based on keyword or type. Location must always be specified,
     * either by passing a LatLngBounds, or location and radius parameters. The PlaceResults returned from the function
     * are subsets of the full PlaceResult. Your can get a more detailed PlaceResult for each place by sending a Place
     * Details request passing the reference value for the desired place. The PlaceSearchPagination object can be used to
     * fetch additional pages of results (null if this is the last page of results or if there is only one page of
     * results).
     * @param {google.maps.places.PlaceSearchRequest} request
     * @returns {Promise<PlaceResultWrapper>}
     */
    GoogleService.prototype.nearbySearch = function (request) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._promise];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, PlaceResultWrapper.wrap(function (callback) {
                                _this._places.nearbySearch(request, callback);
                            })];
                    case 2: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Retrieves the details for a Google Place.
     * @param {google.maps.places.PlaceDetailsRequest} request
     * @returns {Promise<PlaceResult>}
     */
    GoogleService.prototype.placeDetails = function (request) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._promise];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                try {
                                    _this._places.getDetails(request, function (results, status) {
                                        if (status === google.maps.places.PlacesServiceStatus.OK) {
                                            return resolve(results);
                                        }
                                        reject(new Error(status));
                                    });
                                }
                                catch (e) {
                                    reject(e);
                                }
                            })];
                }
            });
        });
    };
    /**
     * Performs a search for activities of the specified type within a radius of a lat/lon.
     * @param type {ActivityType} Activity type
     * @param location {LatLon} Center point for the search
     * @param radius {number} Radius in nautical miles
     * @returns {Promise<PlaceResultWrapper>} Paginated search results
     */
    GoogleService.prototype.searchActivitiesByLatLon = function (type, location, radius) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var request;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._promise];
                    case 1:
                        _a.sent();
                        request = {
                            location: { lat: location.lat, lng: location.lon },
                            radius: convert(radius, NAUTICAL_MILES, METERS)
                        };
                        this._populateActivityRequest(type, request);
                        return [4 /*yield*/, this.nearbySearch(request)];
                    case 2: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Performs a search for activities of the specified type within a bounding box.
     * @param type {ActivityType} Activity type
     * @param bounds {BoundingBox} Bounding box search area
     * @returns {Promise<PlaceResultWrapper>} Paginated search results
     */
    GoogleService.prototype.searchActivitiesByBoundingBox = function (type, bounds) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var area, ne, sw, request;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._promise];
                    case 1:
                        _a.sent();
                        area = bounds.getAreaDegrees();
                        if (area > MAX_BBOX_AREA) {
                            throw new Error("Bounding box area (" + area + " square degrees) exceeds maximum (" + MAX_BBOX_AREA + ").");
                        }
                        ne = { lat: bounds.ne.lat, lng: bounds.ne.lon };
                        sw = { lat: bounds.sw.lat, lng: bounds.sw.lon };
                        request = {
                            bounds: new google.maps.LatLngBounds(sw, ne)
                        };
                        this._populateActivityRequest(type, request);
                        return [4 /*yield*/, this.nearbySearch(request)];
                    case 2: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Populates the given PlaceSearchRequest instance with the details of an activity request
     * @param {ActivityType} type Activity type being searched for
     * @param {google.maps.places.PlaceSearchRequest} request
     * @private
     */
    GoogleService.prototype._populateActivityRequest = function (type, request) {
        var activity = ACTIVITY_FILTERS.get(type);
        if (!activity) {
            throw new Error("Unable to perform activity search: Activity filter with key " + type + " is missing.");
        }
        if (activity.placeType) {
            request.type = activity.placeType;
        }
        else if (activity.keyword) {
            request.keyword = activity.keyword;
        }
        else {
            throw new Error("Activity filter with key " + activity.key + " does not have placeType or keyword set. " +
                "One or the other is required in order to perform an activity search.");
        }
    };
    return GoogleService;
}());
export { GoogleService };
